import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useRecoilValue } from 'recoil';
import authAtom from '~/recoil/atom/auth';
import mediaQueryState from '~/recoil/atom/mediaQueryState';

import { uniq } from 'lodash';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { IoMdClose } from 'react-icons/io';
import { IoAlertCircleOutline, IoPersonCircleOutline } from 'react-icons/io5';
import updatePlatformCustomer from '~/firebase/update/updatePlatformCustomer';
import customerAtom from '~/recoil/atom/customer';
import platformAtom from '~/recoil/atom/platform';
import {
  CHILLI_TASTE_STRING,
  COOKING_STYLE_STRING,
  Customer,
  Gender,
  TZUCHI_EATING_OPTIONS_STRING,
  TZUCHI_SOUR_STRING,
  TZUCHI_TASTE_STRING,
  TZuchiAgeLevel,
} from '~/types/platform/customer';
import { emailRegex } from '~/types/utilities';
import phoneNumberValidation from '~/utils/phonNumberValidation';

const ErrorMsg = ({ msg }: { msg: string }) => {
  return (
    <FormErrorMessage
      p="3px 4px"
      bg="#FFF5F5"
      borderRadius="5px"
      flexDir="row"
      alignItems="flex-start"
    >
      <Icon as={IoAlertCircleOutline} fontSize="24px" color="#E83B3B" mr="5px" />
      <Text fontSize="14px" fontWeight={400} color="#E83B3B">
        {msg}
      </Text>
    </FormErrorMessage>
  );
};

type CustomerInfoEditDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  allowedClose?: boolean;
};

export default function CustomerInfoEditDialog({
  isOpen,
  onClose,
  allowedClose = true,
}: CustomerInfoEditDialogProps) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Customer>();
  const customer = useRecoilValue(customerAtom);
  const platform = useRecoilValue(platformAtom);
  const auth = useRecoilValue(authAtom);
  const { isLargerThan768 } = useRecoilValue(mediaQueryState);
  const toast = useToast({ duration: 1000, position: 'top' });
  const [isLoading, setIsLoading] = useState(false);
  const isError = Object.keys(errors || {})?.length > 0;

  const handleUpdate = handleSubmit(
    async (data) => {
      if (auth) {
        setIsLoading(true);
        try {
          const { formattedPhoneNumber } = phoneNumberValidation(data.phoneNumber || '');
          if (formattedPhoneNumber) {
            data.phoneNumber = formattedPhoneNumber;
          }
          data.cookingStyle = data?.cookingStyle?.length
            ? uniq((data?.cookingStyle || []).map((i) => Number(i)))
            : [];
          data.eatingStyle = data?.eatingStyle?.length
            ? uniq((data?.eatingStyle || []).map((i) => Number(i)))
            : [];
          data.chilliTaste = data?.chilliTaste ? Number(data.chilliTaste) : null;
          data.sourTaste = data?.sourTaste ? Number(data.sourTaste) : null;
          data.taste = data?.taste ? Number(data.taste) : null;
          data.email = data?.email || '';
          data.birthday = data?.birthday || '';
          data.age = data?.age ? Number(data.age) : -1;
          await updatePlatformCustomer(platform.id, auth, { ...data, isLoginFormSubmitted: true });
          onClose();
          toast({ status: 'success', title: '儲存成功' });
        } catch (e) {
          console.error('handleUpdate error', e);
        } finally {
          setIsLoading(false);
        }
      } else {
        toast({ status: 'error', title: '變更儲存失敗' });
      }
    },
    (e) => console.log(e),
  );

  useEffect(() => {
    if (isOpen) {
      reset(customer);
    } else {
      reset();
    }
  }, [isOpen]);

  if (isLargerThan768) {
    return (
      <Modal
        closeOnOverlayClick={allowedClose}
        isOpen={isOpen}
        onClose={onClose}
        scrollBehavior={'inside'}
      >
        <ModalOverlay />
        <ModalContent maxH="80vh" my="auto">
          {allowedClose ? <ModalCloseButton /> : null}
          <ModalHeader mt={4} borderBottomWidth="1px" borderColor="primary">
            <HStack justify="center">
              <Icon as={IoPersonCircleOutline} color="primary" boxSize="1.5rem" />
              <Heading size="md" fontWeight="normal">
                個人資訊
              </Heading>
            </HStack>
          </ModalHeader>
          <ModalBody>
            <VStack spacing="16px" py="20px">
              {!allowedClose && isError ? (
                <FormControl isInvalid>
                  <ErrorMsg msg="有必填項目尚未填寫完成，只需填寫一次，下次不用再填寫" />
                </FormControl>
              ) : null}

              <Controller
                control={control}
                name="displayName"
                rules={{ required: '此為必填項目' }}
                render={({ field, fieldState }) => {
                  return (
                    <FormControl isRequired isInvalid={fieldState.invalid}>
                      <FormLabel fontSize="14px" fontWeight={400}>
                        姓名
                      </FormLabel>
                      <Input {...field} placeholder="請輸入姓名" />
                      {fieldState?.error?.message ? (
                        <ErrorMsg msg={fieldState?.error?.message} />
                      ) : null}
                    </FormControl>
                  );
                }}
              />

              <Controller
                control={control}
                name="phoneNumber"
                rules={{
                  required: '此為必填項目',
                  validate: (v) => {
                    const { error = undefined } = phoneNumberValidation(v || '');
                    if (!!error) return error;
                    return true;
                  },
                }}
                render={({ field, fieldState }) => {
                  return (
                    <FormControl isRequired isInvalid={fieldState.invalid}>
                      <FormLabel fontSize="14px" fontWeight={400}>
                        手機
                      </FormLabel>
                      <Input
                        disabled={!customer?.lineUId}
                        type="tel"
                        inputMode="tel"
                        value={field.value}
                        placeholder="請輸入手機號碼"
                        onChange={(e) => {
                          const newValue = e?.target?.value || '';
                          field.onChange(newValue);
                        }}
                      />
                      {fieldState?.error?.message ? (
                        <ErrorMsg msg={fieldState?.error?.message} />
                      ) : null}
                    </FormControl>
                  );
                }}
              />

              <Controller
                control={control}
                name="gender"
                rules={{ required: '此為必填項目' }}
                render={({ field, fieldState }) => {
                  return (
                    <FormControl isRequired isInvalid={fieldState.invalid}>
                      <FormLabel fontSize="14px" fontWeight={400}>
                        性別
                      </FormLabel>
                      <RadioGroup {...field}>
                        <HStack align="center">
                          <Radio value={Gender.LADY} flex={1}>
                            <Text fontSize="14px" fontWeight={400}>
                              生理女
                            </Text>
                          </Radio>
                          <Radio value={Gender.MALE} flex={1}>
                            <Text fontSize="14px" fontWeight={400}>
                              生理男
                            </Text>
                          </Radio>
                        </HStack>
                      </RadioGroup>
                      {fieldState?.error?.message ? (
                        <ErrorMsg msg={fieldState?.error?.message} />
                      ) : null}
                    </FormControl>
                  );
                }}
              />

              <Controller
                control={control}
                name="age"
                rules={{ required: '此為必填項目' }}
                render={({ field, fieldState }) => {
                  return (
                    <FormControl isRequired isInvalid={fieldState.invalid}>
                      <FormLabel fontSize="14px" fontWeight={400}>
                        年齡區間
                      </FormLabel>
                      <RadioGroup {...field} value={Number(field.value)}>
                        <HStack align="center">
                          <Radio value={TZuchiAgeLevel.From16To30} flex={1}>
                            <Text fontSize="14px" fontWeight={400}>
                              16~30
                            </Text>
                          </Radio>
                          <Radio value={TZuchiAgeLevel.From31To40} flex={1}>
                            <Text fontSize="14px" fontWeight={400}>
                              31~40
                            </Text>
                          </Radio>
                          <Radio value={TZuchiAgeLevel.From41To60} flex={1}>
                            <Text fontSize="14px" fontWeight={400}>
                              41~60
                            </Text>
                          </Radio>
                          <Radio value={TZuchiAgeLevel.From61} flex={1}>
                            <Text fontSize="14px" fontWeight={400}>
                              61以上
                            </Text>
                          </Radio>
                        </HStack>
                      </RadioGroup>
                      {fieldState?.error?.message ? (
                        <ErrorMsg msg={fieldState?.error?.message} />
                      ) : null}
                    </FormControl>
                  );
                }}
              />

              <Controller
                control={control}
                name="email"
                rules={{
                  pattern: {
                    value: emailRegex,
                    message: '請輸入正確的電子信箱',
                  },
                }}
                render={({ field, fieldState }) => {
                  return (
                    <FormControl isInvalid={fieldState.invalid}>
                      <FormLabel fontSize="14px" fontWeight={400}>
                        電子信箱
                      </FormLabel>
                      <Input
                        placeholder="請輸入電子信箱"
                        type="email"
                        inputMode="email"
                        value={field.value}
                        onChange={(e) => {
                          field.onChange(e?.target?.value || '');
                        }}
                      />
                      {fieldState?.error?.message ? (
                        <ErrorMsg msg={fieldState?.error?.message} />
                      ) : null}
                    </FormControl>
                  );
                }}
              />

              <Controller
                control={control}
                name="birthday"
                render={({ field, fieldState }) => {
                  return (
                    <FormControl isInvalid={fieldState.invalid}>
                      <FormLabel fontSize="14px" fontWeight={400}>
                        生日日期
                      </FormLabel>
                      <Input
                        type="date"
                        placeholder="請輸入生日"
                        value={field.value || undefined}
                        onChange={(e) => {
                          const newValue = e?.target?.value || null;
                          field.onChange(newValue);
                        }}
                      />
                    </FormControl>
                  );
                }}
              />

              <Controller
                control={control}
                name="cookingStyle"
                render={({ field, fieldState }) => {
                  return (
                    <FormControl isInvalid={fieldState.invalid}>
                      <FormLabel fontSize="14px" fontWeight={400}>
                        喜歡的料理方式（可複選）
                      </FormLabel>
                      <CheckboxGroup
                        onChange={(v) => field.onChange(v.map((i) => Number(i)))}
                        value={(field.value || []).map((i) => String(i))}
                      >
                        <Grid
                          templateColumns="repeat(auto-fill, minmax(60px, 1fr))"
                          columnGap="30px"
                          rowGap="4px"
                          alignItems="start"
                        >
                          {Object.keys(COOKING_STYLE_STRING).map((v) => {
                            return (
                              <Checkbox key={v} value={String(v)} w="fit-content">
                                <Text
                                  whiteSpace="nowrap"
                                  textAlign="left"
                                  fontSize="14px"
                                  fontWeight={400}
                                >
                                  {COOKING_STYLE_STRING[v]}
                                </Text>
                              </Checkbox>
                            );
                          })}
                        </Grid>
                      </CheckboxGroup>
                      {fieldState?.error?.message ? (
                        <ErrorMsg msg={fieldState?.error?.message} />
                      ) : null}
                    </FormControl>
                  );
                }}
              />

              <FormControl>
                <FormLabel fontSize="14px" fontWeight={400}>
                  口味偏好
                </FormLabel>
                <Controller
                  control={control}
                  name="chilliTaste"
                  render={({ field }) => {
                    return (
                      <RadioGroup
                        onChange={(v) => field.onChange(Number(v))}
                        value={String(field.value)}
                      >
                        <HStack align="center" spacing="4px">
                          {Object.keys(CHILLI_TASTE_STRING).map((v) => {
                            return (
                              <Radio key={v} value={String(v)} flex={1}>
                                <Text whiteSpace="nowrap" fontSize="14px" fontWeight={400}>
                                  {CHILLI_TASTE_STRING[v]}
                                </Text>
                              </Radio>
                            );
                          })}
                        </HStack>
                      </RadioGroup>
                    );
                  }}
                />
                <Divider bg="#EEE" height="1px" my="8px" />
                <Controller
                  control={control}
                  name="sourTaste"
                  render={({ field }) => {
                    return (
                      <RadioGroup
                        onChange={(v) => field.onChange(Number(v))}
                        value={String(field.value)}
                      >
                        <HStack align="center" spacing="4px">
                          {Object.keys(TZUCHI_SOUR_STRING).map((v) => {
                            return (
                              <Radio key={v} value={String(v)} flex={1}>
                                <Text whiteSpace="nowrap" fontSize="14px" fontWeight={400}>
                                  {TZUCHI_SOUR_STRING[v]}
                                </Text>
                              </Radio>
                            );
                          })}
                          <Box flex={1} />
                        </HStack>
                      </RadioGroup>
                    );
                  }}
                />
                <Divider bg="#EEE" height="1px" my="8px" />
                <Controller
                  control={control}
                  name="taste"
                  render={({ field }) => {
                    return (
                      <RadioGroup
                        onChange={(v) => field.onChange(Number(v))}
                        value={String(field.value)}
                      >
                        <HStack align="center" spacing="4px">
                          {Object.keys(TZUCHI_TASTE_STRING).map((v) => {
                            return (
                              <Radio key={v} value={String(v)} flex={1}>
                                <Text whiteSpace="nowrap" fontSize="14px" fontWeight={400}>
                                  {TZUCHI_TASTE_STRING[v]}
                                </Text>
                              </Radio>
                            );
                          })}
                          <Box flex={1} />
                        </HStack>
                      </RadioGroup>
                    );
                  }}
                />
              </FormControl>

              <Controller
                control={control}
                name="eatingStyle"
                render={({ field }) => {
                  return (
                    <FormControl>
                      <FormLabel fontSize="14px" fontWeight={400}>
                        飲食風格（可複選）
                      </FormLabel>
                      <CheckboxGroup
                        onChange={(v) => field.onChange(v.map((i) => Number(i)))}
                        value={(field.value || []).map((i) => String(i))}
                      >
                        <HStack
                          flexWrap="wrap"
                          sx={{ rowGap: '4px', columnGap: '30px' }}
                          alignItems="flex-start"
                          spacing={0}
                        >
                          {Object.keys(TZUCHI_EATING_OPTIONS_STRING).map((v) => {
                            return (
                              <Checkbox key={v} value={String(v)} w="fit-content" minW="120px">
                                <Text whiteSpace="nowrap" fontSize="14px" fontWeight={400}>
                                  {TZUCHI_EATING_OPTIONS_STRING[v]}
                                </Text>
                              </Checkbox>
                            );
                          })}
                        </HStack>
                      </CheckboxGroup>
                    </FormControl>
                  );
                }}
              />
            </VStack>
          </ModalBody>
          <ModalFooter p={0}>
            <Button
              size="lg"
              isLoading={isLoading}
              borderRadius="0"
              isFullWidth
              onClick={handleUpdate}
            >
              確認
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Drawer closeOnOverlayClick={allowedClose} isOpen={isOpen} onClose={onClose} size="full">
      <DrawerOverlay />
      <DrawerContent maxH="100svh">
        {allowedClose ? <DrawerCloseButton /> : null}
        <DrawerHeader mt={4} borderBottomWidth="1px" borderColor="primary">
          <HStack justify="center">
            <Icon as={IoPersonCircleOutline} color="primary" boxSize="1.5rem" />
            <Heading size="md" fontWeight="normal">
              個人資訊
            </Heading>
          </HStack>
        </DrawerHeader>
        <DrawerBody>
          <VStack spacing="16px" py="20px">
            {!allowedClose && isError ? (
              <FormControl isInvalid>
                <ErrorMsg msg="有必填項目尚未填寫完成，只需填寫一次，下次不用再填寫" />
              </FormControl>
            ) : null}

            <Controller
              control={control}
              name="displayName"
              rules={{ required: '此為必填項目' }}
              render={({ field, fieldState }) => {
                return (
                  <FormControl isRequired isInvalid={fieldState.invalid}>
                    <FormLabel fontSize="14px" fontWeight={400}>
                      姓名
                    </FormLabel>
                    <Input {...field} placeholder="請輸入姓名" />
                    {fieldState?.error?.message ? (
                      <ErrorMsg msg={fieldState?.error?.message} />
                    ) : null}
                  </FormControl>
                );
              }}
            />

            <Controller
              control={control}
              name="phoneNumber"
              rules={{
                required: '此為必填項目',
                validate: (v) => {
                  const { error = undefined } = phoneNumberValidation(v || '');
                  if (!!error) return error;
                  return true;
                },
              }}
              render={({ field, fieldState }) => {
                return (
                  <FormControl isRequired isInvalid={fieldState.invalid}>
                    <FormLabel fontSize="14px" fontWeight={400}>
                      手機
                    </FormLabel>
                    <Input
                      disabled={!customer?.lineUId}
                      type="tel"
                      inputMode="tel"
                      value={field.value}
                      placeholder="請輸入手機號碼"
                      onChange={(e) => {
                        const newValue = e?.target?.value || '';
                        field.onChange(newValue);
                        // const { formattedPhoneNumber = undefined } =
                        //   phoneNumberValidation(newValue);
                        // if (formattedPhoneNumber) {
                        //   field.onChange(formattedPhoneNumber);
                        // }
                      }}
                    />
                    {fieldState?.error?.message ? (
                      <ErrorMsg msg={fieldState?.error?.message} />
                    ) : null}
                  </FormControl>
                );
              }}
            />

            <Controller
              control={control}
              name="gender"
              rules={{ required: '此為必填項目' }}
              render={({ field, fieldState }) => {
                return (
                  <FormControl isRequired isInvalid={fieldState.invalid}>
                    <FormLabel fontSize="14px" fontWeight={400}>
                      性別
                    </FormLabel>
                    <RadioGroup {...field}>
                      <HStack align="center">
                        <Radio value={Gender.LADY} flex={1}>
                          <Text fontSize="14px" fontWeight={400}>
                            生理女
                          </Text>
                        </Radio>
                        <Radio value={Gender.MALE} flex={1}>
                          <Text fontSize="14px" fontWeight={400}>
                            生理男
                          </Text>
                        </Radio>
                      </HStack>
                    </RadioGroup>
                    {fieldState?.error?.message ? (
                      <ErrorMsg msg={fieldState?.error?.message} />
                    ) : null}
                  </FormControl>
                );
              }}
            />

            <Controller
              control={control}
              name="age"
              rules={{ required: '此為必填項目' }}
              render={({ field, fieldState }) => {
                return (
                  <FormControl isRequired isInvalid={fieldState.invalid}>
                    <FormLabel fontSize="14px" fontWeight={400}>
                      年齡區間
                    </FormLabel>
                    <RadioGroup {...field} value={Number(field.value)}>
                      <HStack align="center">
                        <Radio value={TZuchiAgeLevel.From16To30} flex={1}>
                          <Text fontSize="14px" fontWeight={400}>
                            16~30
                          </Text>
                        </Radio>
                        <Radio value={TZuchiAgeLevel.From31To40} flex={1}>
                          <Text fontSize="14px" fontWeight={400}>
                            31~40
                          </Text>
                        </Radio>
                        <Radio value={TZuchiAgeLevel.From41To60} flex={1}>
                          <Text fontSize="14px" fontWeight={400}>
                            41~60
                          </Text>
                        </Radio>
                        <Radio value={TZuchiAgeLevel.From61} flex={1}>
                          <Text fontSize="14px" fontWeight={400}>
                            61以上
                          </Text>
                        </Radio>
                      </HStack>
                    </RadioGroup>
                    {fieldState?.error?.message ? (
                      <ErrorMsg msg={fieldState?.error?.message} />
                    ) : null}
                  </FormControl>
                );
              }}
            />

            <Controller
              control={control}
              name="email"
              rules={{
                pattern: {
                  value: emailRegex,
                  message: '請輸入正確的電子信箱',
                },
              }}
              render={({ field, fieldState }) => {
                return (
                  <FormControl isInvalid={fieldState.invalid}>
                    <FormLabel fontSize="14px" fontWeight={400}>
                      電子信箱
                    </FormLabel>
                    <Input
                      placeholder="請輸入電子信箱"
                      type="email"
                      inputMode="email"
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e?.target?.value || '');
                      }}
                    />
                    {fieldState?.error?.message ? (
                      <ErrorMsg msg={fieldState?.error?.message} />
                    ) : null}
                  </FormControl>
                );
              }}
            />

            <Controller
              control={control}
              name="birthday"
              render={({ field, fieldState }) => {
                return (
                  <FormControl isInvalid={fieldState.invalid}>
                    <FormLabel fontSize="14px" fontWeight={400}>
                      生日日期
                    </FormLabel>
                    <InputGroup flex="1">
                      <Input
                        type="date"
                        placeholder="請輸入生日"
                        value={field.value || ''}
                        onChange={(e) => {
                          const newValue = e?.target?.value || null;
                          field.onChange(newValue);
                        }}
                      />
                      <InputRightElement
                        children={
                          field.value ? <IoMdClose onClick={() => field.onChange(null)} /> : null
                        }
                      />
                    </InputGroup>
                  </FormControl>
                );
              }}
            />

            <Controller
              control={control}
              name="cookingStyle"
              render={({ field, fieldState }) => {
                return (
                  <FormControl isInvalid={fieldState.invalid}>
                    <FormLabel fontSize="14px" fontWeight={400}>
                      喜歡的料理方式（可複選）
                    </FormLabel>
                    <CheckboxGroup
                      onChange={(v) => field.onChange(v.map((i) => Number(i)))}
                      value={(field.value || []).map((i) => String(i))}
                    >
                      <Grid
                        templateColumns="repeat(auto-fill, minmax(60px, 1fr))"
                        columnGap="30px"
                        rowGap="4px"
                        alignItems="start"
                      >
                        {Object.keys(COOKING_STYLE_STRING).map((v) => {
                          return (
                            <Checkbox key={v} value={String(v)} w="fit-content">
                              <Text
                                whiteSpace="nowrap"
                                textAlign="left"
                                fontSize="14px"
                                fontWeight={400}
                              >
                                {COOKING_STYLE_STRING[v]}
                              </Text>
                            </Checkbox>
                          );
                        })}
                      </Grid>
                    </CheckboxGroup>
                    {fieldState?.error?.message ? (
                      <ErrorMsg msg={fieldState?.error?.message} />
                    ) : null}
                  </FormControl>
                );
              }}
            />

            <FormControl>
              <FormLabel fontSize="14px" fontWeight={400}>
                口味偏好
              </FormLabel>
              <Controller
                control={control}
                name="chilliTaste"
                render={({ field }) => {
                  return (
                    <RadioGroup
                      onChange={(v) => field.onChange(Number(v))}
                      value={String(field.value)}
                    >
                      <HStack align="center" spacing="4px">
                        {Object.keys(CHILLI_TASTE_STRING).map((v) => {
                          return (
                            <Radio key={v} value={String(v)} flex={1}>
                              <Text whiteSpace="nowrap" fontSize="14px" fontWeight={400}>
                                {CHILLI_TASTE_STRING[v]}
                              </Text>
                            </Radio>
                          );
                        })}
                      </HStack>
                    </RadioGroup>
                  );
                }}
              />
              <Divider bg="#EEE" height="1px" my="8px" />
              <Controller
                control={control}
                name="sourTaste"
                render={({ field }) => {
                  return (
                    <RadioGroup
                      onChange={(v) => field.onChange(Number(v))}
                      value={String(field.value)}
                    >
                      <HStack align="center" spacing="4px">
                        {Object.keys(TZUCHI_SOUR_STRING).map((v) => {
                          return (
                            <Radio key={v} value={String(v)} flex={1}>
                              <Text whiteSpace="nowrap" fontSize="14px" fontWeight={400}>
                                {TZUCHI_SOUR_STRING[v]}
                              </Text>
                            </Radio>
                          );
                        })}
                        <Box flex={1} />
                      </HStack>
                    </RadioGroup>
                  );
                }}
              />
              <Divider bg="#EEE" height="1px" my="8px" />
              <Controller
                control={control}
                name="taste"
                render={({ field }) => {
                  return (
                    <RadioGroup
                      onChange={(v) => field.onChange(Number(v))}
                      value={String(field.value)}
                    >
                      <HStack align="center" spacing="4px">
                        {Object.keys(TZUCHI_TASTE_STRING).map((v) => {
                          return (
                            <Radio key={v} value={String(v)} flex={1}>
                              <Text whiteSpace="nowrap" fontSize="14px" fontWeight={400}>
                                {TZUCHI_TASTE_STRING[v]}
                              </Text>
                            </Radio>
                          );
                        })}
                        <Box flex={1} />
                      </HStack>
                    </RadioGroup>
                  );
                }}
              />
            </FormControl>

            <Controller
              control={control}
              name="eatingStyle"
              render={({ field }) => {
                return (
                  <FormControl>
                    <FormLabel fontSize="14px" fontWeight={400}>
                      飲食風格（可複選）
                    </FormLabel>
                    <CheckboxGroup
                      onChange={(v) => field.onChange(v.map((i) => Number(i)))}
                      value={(field.value || []).map((i) => String(i))}
                    >
                      <HStack
                        flexWrap="wrap"
                        sx={{ rowGap: '4px', columnGap: '30px' }}
                        alignItems="flex-start"
                        spacing={0}
                      >
                        {Object.keys(TZUCHI_EATING_OPTIONS_STRING).map((v) => {
                          return (
                            <Checkbox key={v} value={String(v)} w="fit-content" minW="120px">
                              <Text whiteSpace="nowrap" fontSize="14px" fontWeight={400}>
                                {TZUCHI_EATING_OPTIONS_STRING[v]}
                              </Text>
                            </Checkbox>
                          );
                        })}
                      </HStack>
                    </CheckboxGroup>
                  </FormControl>
                );
              }}
            />
          </VStack>
        </DrawerBody>
        <DrawerFooter p="0">
          <Button
            size="lg"
            isLoading={isLoading}
            borderRadius="0"
            p="0"
            isFullWidth
            onClick={handleUpdate}
          >
            確認
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
